import { differenceInCalendarDays, parseISO } from 'date-fns';

import { AvailableCandidateMark, IAvailableCandidate } from '@types';
import { formatDate } from './date';
import { formatSkillLabel } from './skill';

export type AvailabilityTone = 'now' | 'soon' | 'neutral';

interface IAvailability {
  label: string;
  tone: AvailabilityTone;
  isImmediate: boolean;
  isWithin30Days: boolean;
}

const WITHIN_DAYS = 30;

export const availabilityOf = ({
  availability,
}: IAvailableCandidate): IAvailability => {
  switch (availability.kind) {
    case 'immediately':
      return {
        label: 'Immediately',
        tone: 'now',
        isImmediate: true,
        isWithin30Days: true,
      };
    case 'date': {
      const date = parseISO(availability.date ?? '');
      const isWithin30Days =
        differenceInCalendarDays(date, new Date()) <= WITHIN_DAYS;

      return {
        label: `From ${formatDate(date, 'MMM d')}`,
        tone: 'soon',
        isImmediate: false,
        isWithin30Days,
      };
    }
    case 'text':
      return {
        label: availability.text ?? '',
        tone: 'neutral',
        isImmediate: false,
        isWithin30Days: false,
      };
    case 'notice_period':
      return {
        label: 'After notice period',
        tone: 'neutral',
        isImmediate: false,
        isWithin30Days: false,
      };
    default:
      return {
        label: '',
        tone: 'neutral',
        isImmediate: false,
        isWithin30Days: false,
      };
  }
};

export const allocationNote = ({
  allocation,
}: IAvailableCandidate): string | null =>
  allocation !== null && allocation < 100 ? `${allocation}% allocation` : null;

export const isAgileEngineer = (candidate: IAvailableCandidate): boolean =>
  candidate.category === 'agileengineer';

export type AvailableCandidateRequestKind = 'interview' | 'learnMore' | 'skip';

export const SHOW_CANDIDATE_MARKS = false;

export const toggledMark = (
  { mark }: IAvailableCandidate,
  next: AvailableCandidateMark,
): AvailableCandidateMark | null => (mark === next ? null : next);

export const offersRequest = (
  { requests }: IAvailableCandidate,
  kind: AvailableCandidateRequestKind,
): boolean =>
  !requests.interview && !(kind === 'learnMore' && requests.learnMore);

export const primaryStackOf = ({ primarySkills }: IAvailableCandidate) =>
  primarySkills.map(formatSkillLabel).join(', ');

export const availableCandidateSkills = ({
  primarySkills,
  secondarySkills,
}: IAvailableCandidate): string[] => [...primarySkills, ...secondarySkills];

export const AvailableCategoryLabel: Record<
  IAvailableCandidate['category'],
  string
> = {
  agileengineer: 'AgileEngine bench',
  prescreened: 'Pre-screened candidates',
};
